import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../supabase";
import { chatService, realtimeService, userService } from "../services/supabaseService";
import "../style.css";

const DEFAULT_AVATAR = "https://i.pinimg.com/736x/d4/8f/8f/d48f8f59f2de25750b13084b59301201.jpg";

const ChatToast = ({ friendData, onClose, onExpand }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isVisible, setIsVisible] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const messagesEndRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const chatId = currentUser && friendData ? [currentUser.id, friendData.userId].sort().join('_') : null;

  // Buscar usuário logado do Supabase
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);
      
      // Buscar perfil completo do usuário
      if (user) {
        const profile = await userService.getUserById(user.id);
        setCurrentUserProfile(profile);
      }
    };
    getUser();
  }, []);

  // Carregar mensagens do localStorage + Supabase
  const loadAllMessages = async () => {
    if (!chatId || !currentUser) return;

    try {
      // 1. Carregar mensagens do localStorage
      const localKey = `chat_${chatId}_${currentUser.id}`;
      const localMessages = JSON.parse(localStorage.getItem(localKey) || '[]');

      // 2. Se está conversando consigo mesmo, usar SOMENTE localStorage
      if (friendData.userId === currentUser.id) {
        setMessages(localMessages.sort((a, b) => 
          new Date(a.created_at) - new Date(b.created_at)
        ));
        return;
      }

      // 3. Carregar mensagens do Supabase (para conversas normais)
      const supabaseMessages = await chatService.getMessages(chatId);

      // 4. Processar mensagens recebidas (não lidas ainda)
      const newReceivedMessages = [];
      for (const msg of supabaseMessages) {
        if (msg.sender_id !== currentUser.id && !msg.read) {
          // Marcar como lida
          await chatService.markMessageAsRead(msg.id);
          
          // Preparar para salvar localmente
          newReceivedMessages.push({
            ...msg,
            read: true,
            readAt: new Date().toISOString()
          });
          
          // Deletar do Supabase
          await chatService.deleteMessage(msg.id);
        }
      }

      // 5. Adicionar novas mensagens ao localStorage
      if (newReceivedMessages.length > 0) {
        const updatedLocalMessages = [...localMessages, ...newReceivedMessages];
        localStorage.setItem(localKey, JSON.stringify(updatedLocalMessages));
      }

      // 6. Pegar apenas MINHAS mensagens que ainda estão no Supabase (não lidas pelo outro)
      const myUnreadMessages = supabaseMessages.filter(m => m.sender_id === currentUser.id);

      // 7. Criar um Set para IDs únicos e combinar mensagens
      const messagesMap = new Map();
      
      // Adicionar mensagens locais
      localMessages.forEach(msg => {
        messagesMap.set(msg.id, msg);
      });
      
      // Adicionar novas mensagens recebidas
      newReceivedMessages.forEach(msg => {
        messagesMap.set(msg.id, msg);
      });
      
      // Adicionar minhas mensagens não lidas
      myUnreadMessages.forEach(msg => {
        messagesMap.set(msg.id, msg);
      });

      // 8. Converter Map para array e ordenar
      const allMessages = Array.from(messagesMap.values()).sort((a, b) => 
        new Date(a.created_at) - new Date(b.created_at)
      );

      setMessages(allMessages);
      
      // Scroll para o final após carregar mensagens
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      }, 300);
    } catch (error) {
      console.error("Erro ao carregar mensagens:", error);
    }
  };

  // Auto-close após 5 segundos se não for expandido
  useEffect(() => {
    if (!isExpanded) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => onClose(), 300);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [isExpanded, onClose]);

  // Escuta mensagens do chat
  useEffect(() => {
    if (!chatId || !currentUser) return;

    loadAllMessages();

    // Se está conversando consigo mesmo, não precisa de subscrição em tempo real
    if (friendData.userId === currentUser.id) {
      return;
    }

    // Configurar subscrição em tempo real para conversas normais
    const subscription = realtimeService.subscribeToMessages(chatId, async (payload) => {
      if (payload.eventType === 'INSERT') {
        const newMsg = payload.new;
        
        // Se é mensagem recebida (de outra pessoa)
        if (newMsg.sender_id !== currentUser.id) {
          console.log('📥 Mensagem recebida de outra pessoa:', newMsg.id);
          
          // Salvar no localStorage ANTES de deletar
          const localKey = `chat_${chatId}_${currentUser.id}`;
          const localMessages = JSON.parse(localStorage.getItem(localKey) || '[]');
          const readMsg = {
            ...newMsg,
            read: true,
            readAt: new Date().toISOString()
          };
          localMessages.push(readMsg);
          localStorage.setItem(localKey, JSON.stringify(localMessages));
          console.log('💾 Mensagem salva no localStorage');
          
          // Adicionar direto no state (tempo real)
          setMessages(prev => [...prev, readMsg]);
          
          // Marcar como lida no Supabase
          const marked = await chatService.markMessageAsRead(newMsg.id);
          console.log('✓ Mensagem marcada como lida:', marked);
          
          // Deletar do Supabase imediatamente
          console.log('🗑️ Tentando deletar mensagem do Supabase:', newMsg.id);
          const deleted = await chatService.deleteMessage(newMsg.id);
          if (deleted) {
            console.log('✅ Mensagem deletada com sucesso do Supabase');
          } else {
            console.error('❌ Falha ao deletar mensagem do Supabase');
          }
        } else {
          // Se é minha própria mensagem vinda do Supabase, apenas adicionar no state
          setMessages(prev => {
            // Evitar duplicatas - verifica se já existe pelo ID
            const exists = prev.some(m => m.id === newMsg.id);
            if (exists) return prev;
            return [...prev, newMsg];
          });
        }
      }
      
      // Listener para quando mensagem é marcada como lida (UPDATE)
      if (payload.eventType === 'UPDATE') {
        const updatedMsg = payload.new;
        console.log('✅ Mensagem atualizada (marcada como lida):', updatedMsg);
        
        // Se é minha mensagem que foi lida
        if (updatedMsg.sender_id === currentUser.id && updatedMsg.read) {
          console.log('✓✓ Minha mensagem foi lida!');
          
          // Atualizar no state local
          setMessages(prev => prev.map(m => 
            m.id === updatedMsg.id ? { ...m, read: true, readAt: updatedMsg.updated_at } : m
          ));
          
          // Atualizar no localStorage
          const localKey = `chat_${chatId}_${currentUser.id}`;
          const localMessages = JSON.parse(localStorage.getItem(localKey) || '[]');
          const updatedLocalMessages = localMessages.map(m => 
            m.id === updatedMsg.id ? { ...m, read: true, readAt: updatedMsg.updated_at } : m
          );
          localStorage.setItem(localKey, JSON.stringify(updatedLocalMessages));
          
          // Deletar do Supabase após 1 segundo (dar tempo da outra pessoa ver)
          setTimeout(async () => {
            await chatService.deleteMessage(updatedMsg.id);
            console.log('🗑️ Mensagem lida removida do Supabase');
          }, 1000);
        }
      }
    });

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [chatId, currentUser, friendData.userId]);

  // Scroll para a última mensagem
  useEffect(() => {
    if (messages.length > 0) {
      // Pequeno delay para garantir que o DOM foi atualizado
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [messages]);

  // Scroll automático ao expandir o chat
  useEffect(() => {
    if (isExpanded && messages.length > 0) {
      // Scroll imediato quando expandir
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      }, 200);
    }
  }, [isExpanded]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !currentUser) return;

    try {
      const messageData = {
        chat_id: chatId,
        sender_id: currentUser.id,
        content: newMessage.trim(),
        read: false
      };

      const localKey = `chat_${chatId}_${currentUser.id}`;
      const localMessages = JSON.parse(localStorage.getItem(localKey) || '[]');

      // Se está mandando mensagem para si mesmo, salvar SOMENTE no localStorage
      if (friendData.userId === currentUser.id) {
        const selfMessage = {
          ...messageData,
          id: `local_${Date.now()}`,
          created_at: new Date().toISOString(),
          read: true // Já marca como lida porque é você mesmo
        };
        
        localMessages.push(selfMessage);
        localStorage.setItem(localKey, JSON.stringify(localMessages));
        
        // Adicionar direto no state (tempo real)
        setMessages(prev => [...prev, selfMessage]);
        setNewMessage("");
        console.log("💬 Mensagem para si mesmo salva no localStorage");
        return;
      }

      // Para outras pessoas, envia para o Supabase
      const sentMessage = await chatService.sendMessage(messageData);
      
      // Adicionar direto no state (tempo real) - não espera realtime
      setMessages(prev => {
        // Evitar duplicatas
        const exists = prev.some(m => m.id === sentMessage.id);
        if (exists) return prev;
        return [...prev, sentMessage];
      });
      
      // Salvar no localStorage
      localMessages.push(sentMessage);
      localStorage.setItem(localKey, JSON.stringify(localMessages));
      
      setNewMessage("");
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
    }
  };

  const handleExpand = () => {
    const mobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    setIsExpanded(true);
    if (mobile) {
      setIsFullscreen(true);
      document.body.style.overflow = 'hidden';
    }
    onExpand && onExpand();
  };

  const handleClose = () => {
    setIsVisible(false);
    // restore body scroll if was fullscreen
    if (isFullscreen) {
      document.body.style.overflow = '';
    }
    setIsFullscreen(false);
    setTimeout(() => onClose(), 300);
  };

  const handleDeleteChat = () => {
    if (!chatId || !currentUser) return;
    
    // Deletar mensagens do localStorage
    const localKey = `chat_${chatId}_${currentUser.id}`;
    localStorage.removeItem(localKey);
    
    // Limpar mensagens do estado
    setMessages([]);
    
    // Fechar menu
    setShowMenu(false);
    
    // Opcional: mostrar feedback
    alert('Conversa excluída');
  };

  // restore scroll if component unmounts unexpectedly
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showMenu && !e.target.closest('.chat-toast-menu-button') && !e.target.closest('.chat-toast-menu-dropdown')) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showMenu]);

  // Melhora responsividade com teclado virtual no mobile
  useEffect(() => {
    if (!isFullscreen) return;

    const handleResize = () => {
      // Detecta se teclado virtual está aberto
      const isKeyboardOpen = window.innerHeight < window.screen.height * 0.75;
      
      if (isKeyboardOpen) {
        // Scroll para mostrar o input quando teclado abre
        setTimeout(() => {
          const inputContainer = document.querySelector('.chat-toast-input-container');
          if (inputContainer) {
            inputContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 100);
      }
    };

    const handleFocus = (e) => {
      if (e.target.classList.contains('chat-toast-input')) {
        // Pequeno delay para garantir que o teclado apareça
        setTimeout(() => {
          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('focusin', handleFocus);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('focusin', handleFocus);
    };
  }, [isFullscreen]);

  // Controla as classes do body/html para fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.body.classList.add('chat-fullscreen-open');
      document.documentElement.classList.add('chat-fullscreen-open');
    } else {
      document.body.classList.remove('chat-fullscreen-open');
      document.documentElement.classList.remove('chat-fullscreen-open');
    }

    // Cleanup quando o componente é desmontado
    return () => {
      document.body.classList.remove('chat-fullscreen-open');
      document.documentElement.classList.remove('chat-fullscreen-open');
    };
  }, [isFullscreen]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      sendMessage();
    }
  };

  if (!friendData || !currentUser) return null;

  return (
    <div className={`chat-toast ${!isVisible ? 'chat-toast-hidden' : ''} ${isFullscreen ? 'chat-toast-fullscreen' : ''}`}>
      {/* Cabeçalho do Toast */}
      <div className="chat-toast-header" onClick={!isExpanded ? handleExpand : undefined}>
        <div className="chat-toast-user-info">
          <img 
            src={friendData.photoURL || friendData.avatar || DEFAULT_AVATAR} 
            alt={friendData.nome}
            className="chat-toast-avatar"
          />
          <span className="chat-toast-name">
            {friendData.nome}
            {friendData.verified && (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="#1DA1F2"
                className="verified-badge"
                title="Perfil Verificado"
              >
                <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91s-2.52-1.27-3.91-.81c-.66-1.31-1.91-2.19-3.34-2.19s-2.67.88-3.34 2.19c-1.39-.46-2.9-.2-3.91.81s-1.27 2.52-.81 3.91c-1.31.66-2.19 1.91-2.19 3.34s.88 2.67 2.19 3.34c-.46 1.39-.2 2.9.81 3.91s2.52 1.27 3.91.81c.66 1.31 1.91 2.19 3.34 2.19s2.67-.88 3.34-2.19c1.39.46 2.9.2 3.91-.81s1.27-2.52.81-3.91c1.31.66 2.19-1.91 2.19-3.34zm-11.71 4.2L6.8 12.46l1.41-1.42L10.54 13.38l5.65-5.65 1.42 1.42-7.12 7.05z"/>
              </svg>
            )}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
          <button 
            className="chat-toast-menu-button" 
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
          >
            ⋮
          </button>
          {showMenu && (
            <div className="chat-toast-menu-dropdown">
              <button onClick={(e) => {
                e.stopPropagation();
                handleDeleteChat();
              }}>
                🗑️ Excluir conversa
              </button>
            </div>
          )}
          <button className="chat-toast-close" onClick={handleClose}>
            ✕
          </button>
        </div>
      </div>

      {/* Corpo do Chat (só aparece se expandido) */}
      {isExpanded && (
        <div className="chat-toast-body">
          <div className="chat-toast-messages">
            {messages.map((message, index) => {
              const isOwn = message.sender_id === currentUser.id;
              console.log('🔍 Mensagem:', {
                messageId: message.id,
                senderId: message.sender_id,
                currentUserId: currentUser?.id,
                isOwn: isOwn,
                content: message.content
              });
              
              const senderPhoto = isOwn 
                ? (currentUserProfile?.avatar || DEFAULT_AVATAR)
                : (friendData.photoURL || friendData.avatar || DEFAULT_AVATAR);
              
              return (
                <div 
                  key={`${message.id}-${index}-${message.created_at}`}
                  className={`chat-toast-message ${isOwn ? 'own' : 'other'}`}
                >
                  {!isOwn && (
                    <img 
                      src={senderPhoto}
                      alt="Avatar"
                      className="chat-toast-message-avatar"
                    />
                  )}
                  <div className="chat-toast-message-content">
                    <span className="chat-toast-message-text">{message.content}</span>
                    <span className="chat-toast-message-time">
                      {new Date(message.created_at).toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                      {/* Tick de leitura - só mostra para mensagens enviadas */}
                      {isOwn && (
                        <span style={{ marginLeft: '5px', color: message.read ? '#4fc3f7' : '#999' }}>
                          {message.read ? '✓✓' : '✓'}
                        </span>
                      )}
                    </span>
                  </div>
                  {isOwn && (
                    <img 
                      src={senderPhoto}
                      alt="Avatar"
                      className="chat-toast-message-avatar"
                    />
                  )}
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-toast-input-container">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Digite sua mensagem..."
              className="chat-toast-input"
            />
            <button 
              onClick={sendMessage}
              className="chat-toast-send"
              disabled={!newMessage.trim()}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatToast;